import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaHeadphones, FaBatteryFull, FaMobileAlt, FaMicrochip, FaPlug, FaShieldAlt } from 'react-icons/fa';
import { productService } from '../services/productService';
import { categoryService } from '../services/categoryService';

const AccessoriesPage = () => {
  const [accessories, setAccessories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    const fetchAccessories = async () => {
      setLoading(true);
      try {
        const [catsRes, productsRes] = await Promise.all([
          categoryService.getCategories(),
          productService.getProducts({ limit: 100 })
        ]);

        if (productsRes && productsRes.data) {
          const allCategories = catsRes?.data || [];
          
          // Find main accessories category
          const mainAccCat = allCategories.find(c => 
            c.slug?.toLowerCase().includes('accessor') || 
            c.name?.toLowerCase().includes('accessor')
          );
          
          const mainAccId = mainAccCat?._id;
          
          // Compile set of all accessory categories (main + subcategories)
          const accessoryCatIds = new Set();
          if (mainAccId) {
            accessoryCatIds.add(mainAccId);
          }
          
          allCategories.forEach(c => {
            const parentId = c.parentCategory?._id || c.parentCategory;
            if (parentId === mainAccId && mainAccId) {
              accessoryCatIds.add(c._id);
            } else if (c.slug?.toLowerCase().includes('accessor') || c.name?.toLowerCase().includes('accessor')) {
              accessoryCatIds.add(c._id);
            }
          });

          const mappedAccessories = productsRes.data
            .filter(product => {
              const prodCatId = product.category?._id || product.category;
              return accessoryCatIds.has(prodCatId);
            })
            .map(product => ({
              id: product._id,
              name: product.name,
              category: product.category?.name || 'Accessories',
              price: product.pricing?.salePrice || 0,
              image: product.images?.find(img => img.isPrimary)?.url || product.images?.[0]?.url || 'https://images.unsplash.com/photo-1583863788434-e58a36330cf0?w=300',
              description: product.shortDescription || product.description || '',
            }));

          setAccessories(mappedAccessories);

          // Get unique categories from the loaded accessories
          const uniqueCats = Array.from(new Set(mappedAccessories.map(a => a.category)));
          setAvailableCategories(uniqueCats);
        }
      } catch (error) {
        console.error('Error fetching accessories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAccessories();
  }, []);

  const getCategoryIcon = (catName) => {
    const name = catName.toLowerCase();
    if (name.includes('earbud') || name.includes('headphone') || name.includes('audio')) return <FaHeadphones />;
    if (name.includes('charger') || name.includes('cable') || name.includes('plug') || name.includes('adapter')) return <FaPlug />;
    if (name.includes('power') || name.includes('battery')) return <FaBatteryFull />;
    if (name.includes('cover') || name.includes('case') || name.includes('screen') || name.includes('protect') || name.includes('shield')) return <FaShieldAlt />;
    if (name.includes('watch') || name.includes('wearable') || name.includes('smart')) return <FaMicrochip />;
    return <FaMobileAlt />;
  };

  const categories = [
    { name: 'All', icon: <FaMobileAlt />, count: accessories.length },
    ...availableCategories.map(cat => ({
      name: cat,
      icon: getCategoryIcon(cat),
      count: accessories.filter(a => a.category === cat).length
    }))
  ];

  const filteredAccessories = selectedCategory === 'All'
    ? accessories
    : accessories.filter(a => a.category === selectedCategory);

  return (
    <div className="py-12">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Mobile Accessories</h1>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Complete your mobile experience with our premium accessories collection
          </p>
        </motion.div>

        {/* Categories */}
        <div className="flex flex-wrap gap-3 justify-center mb-12">
          {categories.map((cat) => (
            <button
              key={cat.name}
              onClick={() => setSelectedCategory(cat.name)}
              className={`px-6 py-2 rounded-full flex items-center gap-2 transition-all ${selectedCategory === cat.name
                  ? 'bg-primary-600 text-white shadow-lg'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
            >
              {cat.icon}
              <span>{cat.name}</span>
              <span className={`text-xs ${selectedCategory === cat.name ? 'text-white' : 'text-gray-500'}`}>
                ({cat.count})
              </span>
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 dark:bg-gray-700 h-64 rounded-2xl mb-4"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAccessories.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card group"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-2 right-2 bg-primary-600 text-white px-2 py-1 rounded-lg text-xs">
                    {item.category}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-2 group-hover:text-primary-600 transition-colors">
                    {item.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {item.description}
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-primary-600">
                      RS:{item.price.toLocaleString()}
                    </span>
                    <a
                      href={`https://wa.me/923488477649?text=I'm%20interested%20in%20${encodeURIComponent(item.name)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-green-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-600 transition-colors"
                    >
                      Inquiry
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {!loading && filteredAccessories.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔌</div>
            <h3 className="text-xl font-semibold mb-2">No accessories found</h3>
            <p className="text-gray-600 dark:text-gray-400">Try selecting a different category</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessoriesPage;